import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Card, Table } from "react-bootstrap";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import { Pie, Bar } from "react-chartjs-2";
import { toPng } from "html-to-image";
import data from "../ESG/ESGsel.json";
import comment from "../ESG/ESGcomment.json";

// Chart.js 요소 등록
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
);

function ESGresult() {
  const [type, setType] = useState("");
  const [queryData, setQueryData] = useState([]);
  const [categoryScores, setCategoryScores] = useState({});
  const [totalScore, setTotalScore] = useState(0);
  const [pieData, setPieData] = useState(null);
  const [barData, setBarData] = useState(null);
  const [grade, setGrade] = useState("E");
  const [bgColor, setBgColor] = useState("#ffffff");
  const cardRef = useRef(null); // Card 컴포넌트를 참조하기 위한 useRef 추가

  useEffect(() => {
    // 등급 및 배경색 계산
    let calculateGradeAndColor;
    if (type == "A") {
      calculateGradeAndColor = (totalScore) => {
        if (totalScore >= 95) return { grade: "S", bgColor: "#4CAF50" }; // Green
        if (totalScore >= 81) return { grade: "A+", bgColor: "#8BC34A" }; // Light Green
        if (totalScore >= 66) return { grade: "A", bgColor: "#FFC107" }; // Amber
        if (totalScore >= 51) return { grade: "B", bgColor: "#FF9800" }; // Orange
        return { grade: "C", bgColor: "#F44336" }; // Red
      };
    } else {
      calculateGradeAndColor = (totalScore) => {
        if (totalScore >= 77) return { grade: "S", bgColor: "#4CAF50" }; // Green
        if (totalScore >= 67) return { grade: "A+", bgColor: "#8BC34A" }; // Light Green
        if (totalScore >= 57) return { grade: "A", bgColor: "#FFC107" }; // Amber
        if (totalScore >= 43) return { grade: "B", bgColor: "#FF9800" }; // Orange
        return { grade: "C", bgColor: "#F44336" }; // Red
      };
    }

    const { grade, bgColor } = calculateGradeAndColor(totalScore);
    setGrade(grade);
    setBgColor(bgColor);
  }, [totalScore]);

  useEffect(() => {
    const ESGtype = localStorage.getItem("ESGtype");
    const queryParams = new URLSearchParams(window.location.search);
    const encryptedData = queryParams.get("data"); // 암호화된 'data' 파라미터 가져오기

    if (ESGtype < 40) setType("A");
    else setType("B");

    if (encryptedData) {
      try {
        // Base64 복호화 후 JSON 파싱
        const decodedString = atob(encryptedData); // Base64 복호화
        const parsedData = JSON.parse(decodedString); // JSON으로 변환
        setQueryData(parsedData); // 상태 업데이트
        // console.log("Parsed Data:", parsedData); // 디버깅용 데이터 출력

        const scores = {};
        let total = 0;

        // 그래프 데이터를 위한 변수
        const pieScores = [];
        const barLabels = [];
        const barScores = [];

        data.forEach((item) => {
          // parsedData 배열에서 해당 `pk` 찾기
          const matchingEntry = parsedData.find(
            (entry) => entry.pk === item.pk
          );

          if (matchingEntry) {
            const score = parseInt(
              matchingEntry.score || matchingEntry.value || 0,
              10
            );

            if (item.categoryId !== 5) {
              scores[item.categoryId] = (scores[item.categoryId] || 0) + score;
              total += score;
            } else if ([37, 38, 39].includes(item.pk)) {
              pieScores.push(score);
            } else {
              barLabels.push(item.problem);
              barScores.push(score);
            }
          }
        });

        // console.log("Scores:", scores); // 디버깅용 점수 확인
        setCategoryScores(scores);
        setTotalScore(total);

        // 원형 그래프 데이터 설정
        setPieData({
          labels: ["Scope 1", "Scope 2", "Scope 3"],
          datasets: [
            {
              data: pieScores,
              backgroundColor: ["#FF6384", "#36A2EB", "#FFCE56"],
              hoverBackgroundColor: ["#FF6384", "#36A2EB", "#FFCE56"],
            },
          ],
        });

        // 막대 그래프 데이터 설정
        setBarData({
          labels: barLabels,
          datasets: [
            {
              label: "결과",
              data: barScores,
              backgroundColor: "rgba(75,192,192,0.4)",
              borderColor: "rgba(75,192,192,1)",
              borderWidth: 1,
            },
          ],
        });
      } catch (error) {
        console.error("Error decoding or parsing data:", error);
        setQueryData([]); // 복호화 실패 시 빈 배열로 초기화
      }
    } else {
      console.warn("No encrypted data found in URL");
      setQueryData([]); // 'data'가 없을 때 빈 배열로 초기화
    }
  }, []);

  const saveCardAsImage = () => {
    if (cardRef.current) {
      toPng(cardRef.current, {
        backgroundColor: "#ffffff", // 배경색 흰색 설정
      })
        .then((dataUrl) => {
          const link = document.createElement("a");
          link.href = dataUrl; // 이미지 데이터 URL
          link.download = "card-image.png"; // 저장 파일 이름
          link.click(); // 다운로드 트리거
        })
        .catch((err) => {
          console.error("이미지 저장 중 오류 발생:", err); // 오류 로그 출력
        });
    }
  };

  return (
    <Container fluid className="home-about-section" id="about">
      <Row
        style={{
          justifyContent: "flex-end",
          padding: "10px",
          display: "flex",
          alignItems: "right",
        }}
      >
        <Col md="4"></Col>
        <Col md="10" style={{ textAlign: "center", flex: 1 }}>
          <h1 style={{ fontSize: "2.6em" }}>
            <span className="purple"> 그린몽 환경 경영 평과 결과지 </span>
          </h1>
        </Col>
        <Col
          md="4"
          style={{
            flex: "none",
            display: "flex", // Flexbox 적용
            justifyContent: "flex-end", // 오른쪽 정렬
            alignItems: "center", // 세로 중앙 정렬
            gap: "10px", // 버튼 간 간격 설정
          }}
        >
          <button
            style={{
              padding: "10px 20px",
              backgroundColor: "#4CAF50",
              color: "#fff",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
            }}
            onClick={saveCardAsImage} // 버튼 클릭 시 이미지 저장 함수 호출
          >
            카드 이미지 저장
          </button>
          <button
            style={{
              padding: "10px 15px",
              fontSize: "1em",
              backgroundColor: "#4CAF50",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
            }}
            onClick={() => {
              const currentUrl = window.location.href; // 현재 URL 가져오기
              navigator.clipboard
                .writeText(currentUrl) // URL 클립보드에 복사
                .then(() => alert("URL이 복사되었습니다!"))
                .catch(() => alert("URL 복사에 실패했습니다."));
            }}
          >
            결과 공유
          </button>
        </Col>
      </Row>
      <Row style={{ justifyContent: "center", padding: "10px" }}>
        {/* 좌측 카드: 카테고리별 점수 및 그래프 */}
        <Col md={6} style={{ padding: "20px" }}>
          <Card
            ref={cardRef}
            style={{
              padding: "20px",
              boxShadow: "0px 4px 8px rgba(0,0,0,0.2)",
              backgroundColor: "transparent",
              border: "1px solid white",
              borderRadius: "8px",
            }}
          >
            <Card.Body>
              <Row>
                <Col md={12}>
                  <Card
                    style={{
                      padding: "20px",
                      boxShadow: "0px 4px 8px rgba(0,0,0,0.2)",
                      backgroundColor: bgColor, // 등급별 배경색
                      borderRadius: "8px",
                      textAlign: "center", // 중앙 정렬
                      color: "#fff",
                    }}
                  >
                    <Card.Body>
                      <h3>
                        <strong>통합 등급</strong>
                      </h3>
                      <p>귀사의 환경경영 통합 등급은</p>
                      <h1 style={{ fontSize: "3em" }}>{grade}</h1>
                      <p>로 평가되었습니다.</p>
                      <h5>총점: {totalScore}</h5>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              <Row style={{ marginTop: "20px" }}>
                <Col md={6}>
                  <h5>온실가스 배출량 그래프</h5>
                  {pieData ? (
                    <div
                      style={{
                        height: "30vh",
                        width: "250px",
                        margin: "0 auto", // 중앙 정렬
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Pie
                        data={pieData}
                        options={{
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              position: "bottom",
                              labels: {
                                boxWidth: 20, // 색상 박스 크기 조정
                                padding: 15, // 텍스트 간격
                              },
                            },
                          },
                        }}
                      />
                    </div>
                  ) : (
                    <p>데이터 로드 중...</p>
                  )}
                </Col>
                <Col md={6}>
                  <h5>정량 데이터 표</h5>
                  {barData ? (
                    <div
                      style={{
                        margin: "0 auto",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Table striped bordered hover>
                        <thead>
                          <tr>
                            <th>항목</th>
                            <th>값</th>
                          </tr>
                        </thead>
                        <tbody>
                          {barData.labels.map((label, index) => (
                            <tr key={index}>
                              <td>{label}</td>
                              <td>{barData.datasets[0].data[index]}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <p>데이터 로드 중...</p>
                  )}
                </Col>
              </Row>
              <Row style={{ marginTop: "20px" }}>
                <div
                  style={{
                    marginBottom: "20px",
                    textAlign: "left",
                    padding: "15px", // 내부 여백
                    border: "1px solid #ddd", // 구획 경계선
                    borderRadius: "8px", // 구획 모서리 둥글게
                    backgroundColor: "#f9f9f9", // 구획 배경색
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)", // 구획 그림자
                  }}
                >
                  <p style={{ marginBottom: "10px" }}>
                    <li>
                      {localStorage.getItem("ESGtype") &&
                        comment.find(
                          (item) => item.pk == localStorage.getItem("ESGtype")
                        )?.text}
                    </li>
                  </p>
                </div>
              </Row>
            </Card.Body>
          </Card>
        </Col>
        {/* 우측 카드: 응답 평가 및 개선 방안 */}
        <Col md={6} style={{ padding: "20px" }}>
          <Card
            style={{
              padding: "20px",
              boxShadow: "0px 4px 8px rgba(0,0,0,0.2)",
              backgroundColor: "transparent",
              border: "1px solid white",
              borderRadius: "8px",
            }}
          >
            <Card.Body>
              {Object.entries(categoryScores).map(([categoryId, total]) => {
                // 미흡한 항목 필터링
                const weakPoints = data
                  .filter(
                    (item) =>
                      item.categoryId === parseInt(categoryId) &&
                      queryData.find(
                        (entry) => entry.pk === item.pk && entry.score === 1
                      )
                  )
                  .map((item) => item.problem); // 문제만 추출

                return (
                  <div
                    key={categoryId}
                    style={{
                      marginBottom: "20px",
                      textAlign: "left",
                      padding: "15px", // 내부 여백
                      border: "1px solid #ddd", // 구획 경계선
                      borderRadius: "8px", // 구획 모서리 둥글게
                      backgroundColor: "#f9f9f9", // 구획 배경색
                      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)", // 구획 그림자
                    }}
                  >
                    <h5 style={{ marginBottom: "10px" }}>
                      <strong>
                        {categoryId == 1
                          ? "환경 경영 평가 (총점: 42점)"
                          : categoryId == 2
                          ? "환경 성과 (총점: 54점)"
                          : categoryId == 3
                          ? "공급망 (총점: 3점)"
                          : categoryId == 4
                          ? "친환경 서비스 (총점: 9점)"
                          : "알 수 없음"}
                      </strong>
                    </h5>
                    <p style={{ marginBottom: "10px" }}>
                      <li>
                        귀사의 점수: <strong>{total}점</strong>
                      </li>
                    </p>
                    {weakPoints.length > 0 && (
                      <>
                        <p style={{ color: "red", marginBottom: "5px" }}>
                          미흡한 부분
                        </p>
                        <ul>
                          {weakPoints.map((point, index) => (
                            <li key={index}>{point}</li>
                          ))}
                        </ul>
                      </>
                    )}
                    {weakPoints.length > 0 ? (
                      <>
                        <p style={{ marginBottom: "0px" }}>
                          해당 항목에서의 개선 방향 설정과 노력이 필요합니다.
                        </p>
                        <p style={{ marginBottom: "0px" }}>
                          환경 목표를 명확시 설정하고, 이를 실행할 체계적인
                          시스템을 구축하길 바랍니다.
                        </p>
                      </>
                    ) : (
                      <>
                        <p style={{ color: "green", marginBottom: "0px" }}>
                          해당 항목은 우수한 수준으로 관리되고 있습니다.
                        </p>
                        <p style={{ color: "green", marginBottom: "0px" }}>
                          공급망과 친환경 서비스 관리가 효과적으로 이루어져
                          기업의 지속 가능성과 환경적 책임 이해에 기여하고
                          있습니다.
                        </p>
                        <p style={{ color: "green", marginBottom: "0px" }}>
                          현재 수준을 유지하며, 친환경 기술 도입과 혁신적인
                          서비스 개발을 통해 공급망 전반의 지속 가능성을 더욱
                          강화할 수 있습니다.
                        </p>
                      </>
                    )}
                  </div>
                );
              })}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default ESGresult;
